<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts" generic="T extends object">
import { IconChevronDown, IconChevronUp } from '@tabler/icons-vue';
import {
  computed, getCurrentInstance, ref, useSlots, watch,
} from 'vue';

import ActionButton from '~/components/shared/ActionButton.vue';
import Pagination from '~/components/Pagination';

export type DataTableAction = 'delete' | 'details' | 'edit';

export interface DataTableColumn {
  key: string;
  label: string;
  sort?: boolean;
  width?: string;
}

export interface DataTableProps<T extends object = Record<string, unknown>> {
  actions?: DataTableAction[];
  actionsLabel?: string;
  columns: DataTableColumn[];
  currentPage?: number;
  data?: T[];
  emptyMessage?: string;
  hasNext?: boolean;
  loading?: boolean;
  pageSize?: number;
  paginated?: boolean;
  rowKey?: keyof T;
  selectable?: boolean;
  soft?: boolean;
}

type SortDirection = 'asc' | 'desc' | 'none';

interface SortState {
  column: string;
  direction: SortDirection;
}

const props = withDefaults(defineProps<DataTableProps<T>>(), {
  actions: () => ['details', 'edit', 'delete'],
  actionsLabel: 'Ações',
  currentPage: 1,
  data: () => [],
  emptyMessage: 'Nenhum registro encontrado',
  hasNext: false,
  loading: false,
  pageSize: 10,
  paginated: false,
  rowKey: undefined,
  selectable: false,
  soft: true,
});

const emit = defineEmits<{
  delete: [item: T];
  details: [item: T];
  edit: [item: T];
  'page-change': [page: number];
  'page-size-change': [pageSize: number];
  'row-dblclick': [item: T];
  'selection-change': [items: T[]];
}>();

const sortState = ref<SortState>({
  column: 'none',
  direction: 'none',
});

const instance = getCurrentInstance();
const slots = useSlots();

const selectedKeys = ref<Set<string>>(new Set());
const selectAllCheckboxRef = ref<HTMLInputElement | null>(null);

/* eslint-disable perfectionist/sort-modules -- helpers usados pelos computeds abaixo */
function getCellValue(item: T, key: string): unknown {
  return (item as Record<string, unknown>)[key];
}

function getRowKey(item: T): string {
  if (props.rowKey) {
    return String(item[props.rowKey]);
  }
  if ('id' in item) {
    return String((item as Record<string, unknown>).id);
  }
  return JSON.stringify(item);
}

function emitSelectionChange() {
  const selected = (props.data ?? []).filter((item) => selectedKeys.value.has(getRowKey(item)));
  emit('selection-change', selected);
}

function handleSort(column: string) {
  if (sortState.value.column === column) {
    sortState.value.direction = sortState.value.direction === 'asc' ? 'desc' : 'asc';
  } else {
    sortState.value.column = column;
    sortState.value.direction = 'asc';
  }
}

function sortRows(rows: T[]): T[] {
  const { column, direction } = sortState.value;
  if (column === 'none' || direction === 'none') {
    return rows;
  }

  const isAscending = direction === 'asc';

  return [...rows].sort((first, second) => {
    const firstValue = String(getCellValue(first, column) ?? '').toLowerCase();
    const secondValue = String(getCellValue(second, column) ?? '').toLowerCase();
    const comparison = firstValue.localeCompare(secondValue);

    return isAscending ? comparison : -comparison;
  });
}

const displayData = computed(() => sortRows(props.data ?? []));

const hasExtraActions = computed(() => !!slots['extra-actions']);

const showFiltersBar = computed(() => !!slots.filters || !!slots['filters-actions']);

const showActionsColumn = computed(() => props.actions.length > 0 || hasExtraActions.value);

const showCheckboxColumn = computed(() => props.selectable);

const colspan = computed(() => (
  props.columns.length
  + (showCheckboxColumn.value ? 1 : 0)
  + (showActionsColumn.value ? 1 : 0)
));

const tableClass = computed(() => [
  'table',
  'table-vcenter',
  'card-table',
  'data-table__table',
  props.soft ? 'table-sm' : '',
].filter(Boolean).join(' '));

const isRowDoubleClickEnabled = computed(() => {
  const vnodeProps = instance?.vnode.props ?? {};
  return Boolean(vnodeProps.onRowDblclick ?? vnodeProps['onRow-dblclick']);
});

const allRowsSelected = computed(() => {
  if (displayData.value.length === 0) return false;
  return displayData.value.every((item) => selectedKeys.value.has(getRowKey(item)));
});

const someRowsSelected = computed(() => {
  const selectedOnPage = displayData.value.filter((item) => selectedKeys.value.has(getRowKey(item))).length;
  return selectedOnPage > 0 && selectedOnPage < displayData.value.length;
});

const selectedCount = computed(() => selectedKeys.value.size);

watch([allRowsSelected, someRowsSelected], () => {
  if (!selectAllCheckboxRef.value) return;
  selectAllCheckboxRef.value.indeterminate = someRowsSelected.value;
});

watch(() => props.data, () => {
  selectedKeys.value = new Set();
  emitSelectionChange();
});

function handlePageChange(page: number) {
  emit('page-change', page);
}

function handlePageSizeChange(pageSize: number) {
  emit('page-size-change', pageSize);
}

function handleRowDoubleClick(item: T) {
  emit('row-dblclick', item);
}

function handleRowSelectChange(item: T) {
  const key = getRowKey(item);
  if (selectedKeys.value.has(key)) {
    selectedKeys.value.delete(key);
  } else {
    selectedKeys.value.add(key);
  }
  emitSelectionChange();
}

function handleSelectAllChange() {
  if (allRowsSelected.value) {
    displayData.value.forEach((item) => selectedKeys.value.delete(getRowKey(item)));
  } else {
    displayData.value.forEach((item) => selectedKeys.value.add(getRowKey(item)));
  }
  emitSelectionChange();
}

function isRowSelected(item: T): boolean {
  return selectedKeys.value.has(getRowKey(item));
}

function cellSlotName(columnKey: string) {
  return `cell-${columnKey}`;
}

function hasColumnCellSlot(columnKey: string) {
  return !!slots[cellSlotName(columnKey)];
}

function hasGenericCellSlot() {
  return !!slots.cell;
}
/* eslint-enable perfectionist/sort-modules */
</script>

<template>
  <div class="data-table" :class="{ 'data-table--soft': soft }">
    <div
      v-if="showFiltersBar"
      class="data-table__filters border-bottom py-3"
    >
      <div class="d-flex align-items-end gap-2 flex-wrap">
        <slot name="filters" />
        <slot name="filters-actions" />
      </div>
    </div>

    <div class="table-responsive table-with-sticky-actions">
      <table :class="tableClass">
        <thead>
          <tr>
            <th
              v-if="showCheckboxColumn"
              class="data-table__checkbox-column text-secondary"
            >
              <label class="form-check mb-0">
                <input
                  ref="selectAllCheckboxRef"
                  type="checkbox"
                  class="form-check-input"
                  :checked="allRowsSelected"
                  :disabled="loading || displayData.length === 0"
                  @change="handleSelectAllChange"
                />
                <span class="visually-hidden">Selecionar todos</span>
              </label>
            </th>
            <th
              v-for="column in columns"
              :key="column.key"
              class="text-secondary"
              :class="{ 'cursor-pointer': column.sort }"
              :style="column.width ? { width: column.width, minWidth: column.width } : undefined"
              @click="column.sort ? handleSort(column.key) : undefined"
            >
              <div class="d-flex align-items-center justify-content-between break-anywhere">
                <span>{{ column.label }}</span>
                <div
                  v-if="column.sort"
                  class="d-flex flex-column cursor-pointer border rounded-1 mx-2"
                >
                  <IconChevronUp
                    class="icon w-3 h-3"
                    :class="{
                      'text-primary': sortState.column === column.key
                        && sortState.direction === 'asc',
                    }"
                    :size="12"
                    @click.stop="handleSort(column.key)"
                  />
                  <IconChevronDown
                    class="icon w-3 h-3"
                    :class="{
                      'text-primary': sortState.column === column.key
                        && sortState.direction === 'desc',
                    }"
                    :size="12"
                    @click.stop="handleSort(column.key)"
                  />
                </div>
              </div>
            </th>
            <th v-if="showActionsColumn" class="sticky-actions-column text-secondary">
              <slot name="actions-header">
                {{ actionsLabel }}
              </slot>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="loading">
            <tr v-for="rowIndex in 3" :key="`loading-${rowIndex}`">
              <td v-if="showCheckboxColumn">
                <div class="placeholder-glow">
                  <span class="placeholder col-12 data-table__placeholder" />
                </div>
              </td>
              <td v-for="column in columns" :key="column.key">
                <div class="placeholder-glow">
                  <span class="placeholder col-12 data-table__placeholder" />
                </div>
              </td>
              <td v-if="showActionsColumn" class="sticky-actions-column">
                <div class="placeholder-glow">
                  <span class="placeholder col-8 data-table__placeholder" />
                </div>
              </td>
            </tr>
          </template>
          <template v-else-if="displayData.length > 0">
            <tr
              v-for="(item, rowIndex) in displayData"
              :key="getRowKey(item)"
              :class="{ 'data-table__row--clickable': isRowDoubleClickEnabled }"
              @dblclick="handleRowDoubleClick(item)"
            >
              <td v-if="showCheckboxColumn" class="data-table__checkbox-column">
                <label class="form-check mb-0">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="isRowSelected(item)"
                    @change="handleRowSelectChange(item)"
                    @dblclick.stop
                  />
                  <span class="visually-hidden">Selecionar item {{ rowIndex + 1 }}</span>
                </label>
              </td>
              <td
                v-for="column in columns"
                :key="column.key"
              >
                <slot
                  v-if="hasColumnCellSlot(column.key)"
                  :name="cellSlotName(column.key)"
                  :item="item"
                  :column="column"
                />
                <slot
                  v-else-if="hasGenericCellSlot()"
                  name="cell"
                  :item="item"
                  :column="column"
                />
                <span v-else class="text-secondary">
                  {{ getCellValue(item, column.key) ?? '—' }}
                </span>
              </td>
              <td v-if="showActionsColumn" class="sticky-actions-column">
                <div class="d-flex gap-1" @dblclick.stop>
                  <slot name="extra-actions" :item="item" :index="rowIndex" />
                  <ActionButton
                    v-if="actions.includes('details')"
                    type="details"
                    @click="emit('details', item)"
                  />
                  <ActionButton
                    v-if="actions.includes('edit')"
                    type="edit"
                    @click="emit('edit', item)"
                  />
                  <ActionButton
                    v-if="actions.includes('delete')"
                    type="delete"
                    @click="emit('delete', item)"
                  />
                </div>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td :colspan="colspan">
                <div class="d-flex justify-content-center data-table__empty">
                  <slot name="empty">
                    <span class="text-secondary">{{ emptyMessage }}</span>
                  </slot>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div
      v-if="selectable && selectedCount > 0"
      class="data-table__selection-bar text-secondary"
    >
      <strong>{{ selectedCount }}</strong>
      {{ selectedCount === 1 ? 'item selecionado' : 'itens selecionados' }}
    </div>

    <Pagination
      v-if="paginated"
      :current-page="currentPage"
      :has-next="hasNext"
      :page-size="pageSize"
      @page-change="handlePageChange"
      @page-size-change="handlePageSizeChange"
    />
  </div>
</template>

<style scoped lang="scss">
$data-table-header-bg: var(--tblr-bg-surface-secondary, #f8f9fa);

.data-table__table {
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;

  th,
  td {
    vertical-align: middle;
    border-bottom: 1px solid var(--tblr-border-color);
  }

  thead th {
    background-color: $data-table-header-bg;
  }

  tbody tr {
    background-color: var(--tblr-bg-surface, #fff);

    &:hover {
      td {
        background-color: $data-table-header-bg !important;
      }
    }
  }

  .sticky-actions-column {
    border-left: 1px solid var(--tblr-border-color);
    box-shadow: none;
  }
}

.data-table__checkbox-column {
  width: 40px;
  min-width: 40px;
  max-width: 40px;
}

.data-table__row--clickable {
  cursor: pointer;
}

.data-table__selection-bar {
  margin-top: 0.75rem;
  font-size: 0.875rem;
}

.data-table__placeholder {
  height: 12px;
}

.data-table__empty {
  padding: 0.75rem 0;
}

.data-table__filters {
  padding-left: var(--tblr-card-spacer-x, 1.25rem);
  padding-right: var(--tblr-card-spacer-x, 1.25rem);

  :deep(.form-label) {
    margin-bottom: 0.25rem;
  }
}

.data-table--soft {
  .data-table__filters {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;

    :deep(.form-label) {
      font-size: 0.75rem;
      margin-bottom: 0.15rem;
      color: var(--tblr-secondary, #8a939b);
    }

    :deep(.form-control),
    :deep(.form-select) {
      font-size: 0.8125rem;
      padding: 0.25rem 0.5rem;
      min-height: auto;
    }

    :deep(.btn) {
      font-size: 0.8125rem;
      padding: 0.25rem 0.5rem;
      min-height: auto;
    }

    :deep(.mb-3) {
      margin-bottom: 0.5rem !important;
    }
  }

  .data-table__table {
    font-size: 0.8125rem;
    color: var(--tblr-secondary, #5c6670);

    th,
    td {
      padding: 0.35rem 0.5rem;
      border-bottom-color: var(--tblr-border-color-translucent, #f0f2f5);
    }

    thead th {
      font-size: 0.75rem;
      font-weight: 500;
      letter-spacing: 0.02em;
      text-transform: none;
      color: var(--tblr-secondary, #8a939b);
      background-color: $data-table-header-bg;
      border-bottom: 1px solid var(--tblr-border-color);
    }

    tbody tr {
      background-color: transparent !important;

      &:hover {
        background-color: var(--tblr-bg-surface-secondary, #f6f8fa) !important;
      }

      &:last-child td {
        border-bottom: none;
      }
    }

    tbody td {
      color: var(--tblr-secondary, #5c6670);
    }
  }

  .data-table__empty {
    padding: 0.5rem 0;
    font-size: 0.8125rem;
  }

  .data-table__selection-bar {
    margin-top: 0.5rem;
    font-size: 0.8125rem;
  }

  :deep(.sticky-actions-column) {
    background-color: var(--tblr-bg-surface, #fff);
    border-left: 1px solid var(--tblr-border-color);
    box-shadow: none;
  }

  thead :deep(.sticky-actions-column) {
    background-color: $data-table-header-bg;
  }

  tbody tr:hover :deep(.sticky-actions-column) {
    background-color: var(--tblr-bg-surface-secondary, #f6f8fa) !important;
  }

  :deep(.action-btn) {
    padding: 0.2rem;
    border-radius: 0.25rem;

    .icon {
      width: 18px;
      height: 18px;
    }
  }

  :deep(.d-flex.justify-content-end.gap-2.my-3) {
    margin-top: 0.5rem !important;
    margin-bottom: 0.25rem !important;
    padding-right: 0 !important;
    font-size: 0.8125rem;

    .form-label {
      font-size: 0.75rem;
      margin-bottom: 0.15rem;
      color: var(--tblr-secondary, #8a939b);
    }

    .form-select,
    .btn {
      font-size: 0.8125rem;
      padding: 0.25rem 0.5rem;
      min-height: auto;
    }
  }
}

.cursor-pointer {
  cursor: pointer;
}

.break-anywhere {
  word-break: break-word;
}

.w-3 {
  width: 0.75rem;
}

.h-3 {
  height: 0.75rem;
}
</style>
